import Button from "../../../../shared/components/elements/Button";
import { useLessonProgress } from "../../hooks/useLessonProgress";
import { useLessonSession } from "../../hooks/useLessonSession";
import type { LessonContent } from "../../lessonTypes";
import { LESSON_MESSAGES } from "../../messages";

import CompletionFlow from "./CompletionFlow";
import ContentMessage from "./ContentMessage";
import FooterLesson from "./FooterLesson";
import HeaderLesson from "./HeaderLesson";
import LessonScreen from "./LessonScreen";

interface LessonViewProps {
    content?: LessonContent | null;
    userId?: number | string;
    courseId: string;
    contentId: string;
    lessonAttemptId?: number | string | null; // Null on replays, where it is never used
    alreadyCompleted?: boolean;
    onExit: () => void;
}

/**
 * Renders a lesson and its ending:
 * lesson screens -> lesson complete -> (streak screen, if the streak increased) -> onExit
 */
const LessonView = ({
                        content,
                        userId,
                        courseId,
                        contentId,
                        lessonAttemptId,
                        alreadyCompleted = false,
                        onExit
                    }: LessonViewProps) => {
    const {
        currentScreen,
        currentScreenIndex,
        isScreenComplete,
        isLastScreen,
        isFinished,
        progress,
        handleBlockComplete,
        goNext,
        goPrevious
    } = useLessonProgress(content?.fragments);

    const { recordAnswer, completeLesson, completing, completeError, outOfEnergy, result } = useLessonSession({
        userId,
        courseId,
        contentId,
        lessonAttemptId,
        alreadyCompleted
    });

    // On the last screen, save the result first, then open the finish screens
    const handleNext = async () => {
        // A failed save shows its error above the footer, and the learner can try again
        if (isLastScreen && !(await completeLesson())) return;
        goNext();
    };

    // The finish screens come first: nothing else may replace them once the result is saved
    if (isFinished) {
        return <CompletionFlow result={result} showRewards={!alreadyCompleted} onExit={onExit} />;
    }

    if (!currentScreen) return <ContentMessage message={LESSON_MESSAGES.noContent} />;

    if (outOfEnergy) {
        return (
            <ContentMessage
                message={LESSON_MESSAGES.outOfEnergy}
                footer={
                    <div className="flex justify-center py-5">
                        <Button onClick={onExit}>{LESSON_MESSAGES.back}</Button>
                    </div>
                }
            />
        );
    }

    return (
        <div className="min-h-screen flex flex-col">
            <HeaderLesson progress={progress} />

            <main className="flex-1 w-full">
                <LessonScreen
                    screen={currentScreen}
                    onBlockComplete={handleBlockComplete}
                    onAnswer={recordAnswer}
                />
            </main>

            {completeError && (
                <p role="alert" className="py-2 text-center text-red-500">
                    {completeError}
                </p>
            )}

            <FooterLesson
                goPrevious={goPrevious}
                goNext={handleNext}
                hasPrevious={currentScreenIndex > 0}
                hasNext={isScreenComplete && !completing}
                isLastScreen={isLastScreen}
            />
        </div>
    );
};

export default LessonView;